'use client';

import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { queryClientAtom } from 'jotai-tanstack-query';
import { PropsWithChildren } from 'react';

import { getHealthCheckApi } from '@flipster/apis/v2/health';
import { R230522Provider } from '@flipster/features/src/r230522/provider';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { usePathname } from '@flipster/i18n/routing';
import { weakR230522SocketBunja } from '@flipster/r230522/original/socket';
import { PathnameScope, RouterScope } from '@flipster/react/providers/route';
import { checkAxiosError } from '@flipster/utils/axios';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

const websocketOnlyPaths = [
  /^\/support\/contracts\/.*$/, // will be deprecated
  /^\/support\/trading-rules\/.*$/, // support/contracts -> support/trading-rules
  /^\/support\/spot-pairs\/.*$/,
  /^\/event\/competition\/.*$/,
];

export const WebR230522Provider = ({ children }: PropsWithChildren) => {
  const pathname = usePathname();

  // context: https://prextech.slack.com/archives/C07BZ2P9BHR/p1756094900016749?thread_ts=1753771992.514069&cid=C07BZ2P9BHR
  // 일반구독이 적용되기 전에는 contract 페이지에서 브릿지를 통한 ticker 구독이 불가능했음
  // 3.3 버전 이후로는 가능할 걸로 생각되지만 테스트가 필요하기 때문에 당분간은 websocket 전용으로 유지
  const preferredSocketType = websocketOnlyPaths.some((regex) => regex.test(pathname)) ? 'websocket' : undefined;

  return (
    <R230522Provider preferredSocketType={preferredSocketType}>
      {children}
      <R230522Hook />
    </R230522Provider>
  );
};

const R230522Hook = () => {
  useBunja(r230522HookBunja);
  return null;
};
const r230522HookBunja = bunja(() => {
  bunja.use(checkSessionExpiredBunja);
  bunja.use(healthCheckOnSocketErrorBunja);
  bunja.use(refreshNotiBunja);
});
r230522HookBunja.debugLabel = 'r230522HookBunja';

const checkSessionExpiredBunja = bunja(() => {
  const { r230522Socket } = bunja.use(weakR230522SocketBunja);
  const { logout } = bunja.use(authBunja);
  bunja.effect(() => r230522Socket.on('sessionExpired', logout));
});
checkSessionExpiredBunja.debugLabel = 'checkSessionExpiredBunja';

const healthCheckOnSocketErrorBunja = bunja(() => {
  const { r230522Socket } = bunja.use(weakR230522SocketBunja);
  const pathname = bunja.use(PathnameScope);
  const router = bunja.use(RouterScope);
  if (pathname === '/maintenance') return;
  bunja.effect(() => {
    return r230522Socket.on('error', () => {
      getHealthCheckApi().catch((err) => {
        if (checkAxiosError(err) && err.status === 503) router.push('/maintenance');
      });
    });
  });
});
healthCheckOnSocketErrorBunja.debugLabel = 'healthCheckOnSocketErrorBunja';

const refreshNotiBunja = bunja(() => {
  const { r230522Events } = bunja.use(weakR230522SocketBunja);
  const store = bunja.use(JotaiStoreScope);
  bunja.effect(() => {
    return r230522Events.on('private/noti', () => {
      const queryClient = store.get(queryClientAtom);
      queryClient.invalidateQueries({ queryKey: ['getUserNotifications'] });
    });
  });
});
refreshNotiBunja.debugLabel = 'refreshNotiBunja';
